import React, { memo, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import type { PaperProps } from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import dayjs, { type Dayjs } from 'dayjs';
import Draggable from 'react-draggable';
import type { PayRuleDB } from 'repository/timekeeper/@types/PayRuleModel';
import { EmployeeService } from 'services/EmployeeService';
import { usePayRuleData, type PayRuleRow, type PayRuleFormValues } from './hooks/usePayRuleData';

// Constantes y servicio

const INDEFINITE_DATE = dayjs('3000-01-01');
const employeeService = new EmployeeService();

const SEGMENT_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0097a7', '#e64a19', '#558b2f', '#6d4c41', '#00838f'];

// Props del componente

type Props = {
    open: boolean;
    setOpen: (v: boolean) => void;
    data: { id: number; full_name?: string; created_at?: string; [key: string]: any };
    cleanSelected: () => void;
    reloading: () => void;
};

type PayRuleRowWithIndex = PayRuleRow & { _index: number; _rowKey: string };

// Validación pura (fuera del componente para mayor estabilidad)
// Reglas:
//   - pay_rule_id, start_date, end_date son obligatorios
//   - end_date > start_date (el mismo día NO es válido)
//   - los períodos deben ser estrictamente contiguos (sin huecos, sin solapamientos)

function validateRowFn(row: PayRuleRow | undefined, index: number, allRows: PayRuleRow[], t: TFunction): string | null {
    if (!row) return null;
    if (!row.pay_rule_id) return t('timekeeper.employees.dialog.assign_pay_rule.select_required');
    if (!row.start_date) return t('timekeeper.employees.dialog.assign_pay_rule.start_date_required');
    if (!row.end_date) return t('timekeeper.employees.dialog.assign_pay_rule.end_date_required');

    const s = row.start_date as Dayjs;
    const e = row.end_date as Dayjs;

    if (!s.isValid() || !e.isValid()) {
        return t('timekeeper.employees.dialog.assign_pay_rule.start_date_required');
    }

    if (!e.isAfter(s, 'day')) {
        return t('timekeeper.employees.dialog.assign_pay_rule.date_range_invalid');
    }

    // Ordenar todas las filas y encontrar la posición de esta fila
    const sorted = [...allRows.map((r, i) => ({ r, fi: i }))].sort((a, b) => {
        const da = toDayjs(a.r.start_date);
        const db = toDayjs(b.r.start_date);
        if (!da.isValid() || !db.isValid()) return 0;
        return da.valueOf() - db.valueOf();
    });
    const myPos = sorted.findIndex((x) => x.fi === index);

    // Verificar contigüidad con la fila anterior
    if (myPos > 0) {
        const prev = sorted[myPos - 1].r;
        const prevEnd = toDayjs(prev.end_date);
        if (prevEnd.isValid() && !s.isSame(prevEnd.add(1, 'day'), 'day')) {
            if (s.isBefore(prevEnd.add(1, 'day'))) {
                return t('timekeeper.employees.dialog.assign_pay_rule.date_overlap');
            }
            return t('timekeeper.employees.dialog.assign_pay_rule.gap_error');
        }
    }

    // Verificar contigüidad con la fila siguiente
    if (myPos >= 0 && myPos < sorted.length - 1) {
        const next = sorted[myPos + 1].r;
        const nextStart = toDayjs(next.start_date);
        if (nextStart.isValid() && !e.isSame(nextStart.subtract(1, 'day'), 'day')) {
            if (e.isAfter(nextStart.subtract(1, 'day'))) {
                return t('timekeeper.employees.dialog.assign_pay_rule.date_overlap');
            }
            return t('timekeeper.employees.dialog.assign_pay_rule.gap_error');
        }
    }

    return null;
}

// Convierte cualquier valor almacenado por react-hook-form a Dayjs,
// manejando tanto instancias reales como objetos planos clonados internamente.
function toDayjs(val: unknown): Dayjs {
    if (dayjs.isDayjs(val)) return val as Dayjs;
    const $d = (val as any)?.$d;
    if ($d instanceof Date) return dayjs($d);
    return dayjs(val as any);
}

type PayRuleTimelineProps = {
    rows: PayRuleRowWithIndex[]; // ordenadas ascendentemente por start_date
    payRules: PayRuleDB[];
    hireDate: Dayjs;
    onBoundaryClick?: (dotIdx: number, anchorEl: HTMLElement) => void;
    onSegmentClick?: (formIndex: number, anchorEl: HTMLElement) => void;
};

const PayRuleTimeline: React.FC<PayRuleTimelineProps> = ({ rows, payRules, hireDate, onBoundaryClick, onSegmentClick }) => {
    if (rows.length === 0) return null;

    const n = rows.length;
    const segWidth = 100 / n;

    const colorByRule: Record<string, string> = {};
    let ci = 0;
    rows.forEach((row) => {
        const key = String(row.pay_rule_id);
        if (row.pay_rule_id && !colorByRule[key]) {
            colorByRule[key] = SEGMENT_COLORS[ci++ % SEGMENT_COLORS.length];
        }
    });

    const getColor = (pay_rule_id: number | '') => (pay_rule_id ? (colorByRule[String(pay_rule_id)] ?? '#90a4ae') : '#90a4ae');

    // n+1 puntos: la posición i está en i * segWidth %
    const dotPositions = Array.from({ length: n + 1 }, (_, i) => i * segWidth);

    return (
        <Box sx={{ px: 1, py: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            {/* Rule name labels above each segment */}
            <Box sx={{ display: 'flex', mb: 0.5 }}>
                {rows.map((row, i) => {
                    const rule = payRules.find((r) => Number(r.pay_rule_id) === Number(row.pay_rule_id));
                    const color = getColor(row.pay_rule_id);
                    const isSegmentEditable = !!onSegmentClick;
                    const labelNode = (
                        <Typography
                            variant="caption"
                            onClick={isSegmentEditable ? (e: React.MouseEvent<HTMLElement>) => onSegmentClick!(row._index, e.currentTarget) : undefined}
                            onKeyDown={
                                isSegmentEditable
                                    ? (e: React.KeyboardEvent<HTMLElement>) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              onSegmentClick!(row._index, e.currentTarget);
                                          }
                                      }
                                    : undefined
                            }
                            role={isSegmentEditable ? 'button' : undefined}
                            tabIndex={isSegmentEditable ? 0 : undefined}
                            sx={{
                                fontWeight: 700,
                                color,
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: isSegmentEditable ? 'pointer' : 'default',
                                '&:hover': isSegmentEditable ? { textDecoration: 'underline' } : {},
                            }}
                        >
                            {rule?.name ?? '—'}
                        </Typography>
                    );
                    return (
                        <Box key={i} sx={{ width: `${segWidth}%`, textAlign: 'center', px: 0.5, overflow: 'hidden' }}>
                            {isSegmentEditable ? (
                                <Tooltip title="Click para cambiar regla" placement="top">
                                    {labelNode}
                                </Tooltip>
                            ) : (
                                labelNode
                            )}
                        </Box>
                    );
                })}
            </Box>

            {/* Colored bar with boundary dots */}
            <Box sx={{ position: 'relative', height: 20, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', width: '100%', height: 10, borderRadius: 1, overflow: 'hidden' }}>
                    {rows.map((row, i) => (
                        <Box
                            key={i}
                            sx={{
                                width: `${segWidth}%`,
                                bgcolor: getColor(row.pay_rule_id),
                                height: '100%',
                            }}
                        />
                    ))}
                </Box>

                {dotPositions.map((leftPct, i) => {
                    const isFirst = i === 0;
                    const isLast = i === n;
                    const isEditable = !isFirst && !isLast && !!onBoundaryClick;
                    const label = isFirst ? (hireDate.isValid() ? hireDate.format('DD/MM/YYYY') : '') : isLast ? '∞ 3000-01-01' : ((rows[i].start_date as Dayjs)?.format('DD/MM/YYYY') ?? '');

                    const borderColor = isFirst ? '#1565c0' : isLast ? '#9e9e9e' : rows[i]?.pay_rule_id ? getColor(rows[i].pay_rule_id) : '#90a4ae';

                    return (
                        <Tooltip key={i} title={isEditable ? `${label} — clic para editar` : label} placement="top">
                            <Box
                                onClick={isEditable ? (e: React.MouseEvent<HTMLElement>) => onBoundaryClick!(i, e.currentTarget) : undefined}
                                sx={{
                                    position: 'absolute',
                                    left: `${leftPct}%`,
                                    transform: 'translateX(-50%)',
                                    width: isFirst || isLast ? 14 : 12,
                                    height: isFirst || isLast ? 14 : 12,
                                    borderRadius: '50%',
                                    bgcolor: isFirst ? 'primary.dark' : isLast ? '#bdbdbd' : 'background.paper',
                                    border: '2.5px solid',
                                    borderColor,
                                    zIndex: 2,
                                    cursor: isEditable ? 'pointer' : 'default',
                                    boxShadow: isFirst ? '0 0 0 3px rgba(25,118,210,0.2)' : 'none',
                                    transition: 'transform 0.15s',
                                    '&:hover': isEditable ? { transform: 'translateX(-50%) scale(1.35)', zIndex: 3 } : {},
                                }}
                            />
                        </Tooltip>
                    );
                })}
            </Box>

            {/* Date labels below */}
            <Box sx={{ position: 'relative', height: 22, mt: 0.25 }}>
                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        left: 0,
                        fontSize: '0.6rem',
                        color: 'primary.main',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {hireDate.isValid() ? hireDate.format('DD/MM/YYYY') : ''}
                </Typography>

                {rows.slice(1).map((row, i) => {
                    const leftPct = (i + 1) * segWidth;
                    const d = row.start_date as Dayjs;
                    return (
                        <Typography
                            key={i}
                            variant="caption"
                            sx={{
                                position: 'absolute',
                                left: `${leftPct}%`,
                                transform: 'translateX(-50%)',
                                fontSize: '0.6rem',
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {d?.isValid() ? d.format('DD/MM/YYYY') : ''}
                        </Typography>
                    );
                })}

                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        right: 0,
                        fontSize: '0.6rem',
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                    }}
                >
                    ∞ 3000
                </Typography>
            </Box>
        </Box>
    );
};

// Props compartidas de las celdas

type CellSharedProps = {
    rowIndex: number;
    control: Control<PayRuleFormValues>;
    loading: boolean;
    validateRow: (row: PayRuleRow | undefined, index: number, allRows: PayRuleRow[]) => string | null;
    t: TFunction;
    allRows: PayRuleRow[];
};

// Celda selector de regla de pago

type PayRuleSelectCellProps = CellSharedProps & { payRules: PayRuleDB[] };

const PayRuleSelectCell = memo<PayRuleSelectCellProps>(({ rowIndex, control, loading, payRules, validateRow, t, allRows }) => (
    <Controller
        name={`pay_rules.${rowIndex}.pay_rule_id`}
        control={control}
        rules={{
            validate: (value) => !!value || t('timekeeper.employees.dialog.assign_pay_rule.select_required'),
        }}
        render={({ field }) => {
            const validationError = validateRow(allRows[rowIndex], rowIndex, allRows);
            const selectedOption = payRules.find((r) => r.pay_rule_id === field.value) ?? null;
            return (
                <Autocomplete
                    sx={{ mt: validationError ? 3 : 5.5 }}
                    id={`pay_rule_select_${rowIndex}`}
                    autoHighlight
                    options={payRules}
                    getOptionLabel={(option) => option.name}
                    value={selectedOption}
                    onChange={(_, newValue) => field.onChange(newValue?.pay_rule_id ?? '')}
                    disabled={loading}
                    fullWidth
                    size="small"
                    renderInput={(params) => (
                        <TextField {...params} label={t('timekeeper.employees.dialog.assign_pay_rule.rule_label')} error={!!validationError} helperText={validationError || ''} inputRef={field.ref} />
                    )}
                />
            );
        }}
    />
));

// Celda de fecha

type DateCellProps = CellSharedProps & {
    fieldName: 'start_date' | 'end_date';
    isLocked?: boolean;
    onDateChange?: (val: Dayjs | null) => void;
};

const DateCell = memo<DateCellProps>(({ rowIndex, fieldName, control, loading, validateRow, t, allRows, isLocked, onDateChange }) => {
    const isEndDate = fieldName === 'end_date';
    const labelKey = isEndDate ? 'timekeeper.employees.dialog.assign_pay_rule.end_date_label' : 'timekeeper.employees.dialog.assign_pay_rule.start_date_label';
    const requiredKey = isEndDate ? 'timekeeper.employees.dialog.assign_pay_rule.end_date_required' : 'timekeeper.employees.dialog.assign_pay_rule.start_date_required';

    return (
        <Controller
            name={`pay_rules.${rowIndex}.${fieldName}`}
            control={control}
            rules={{ required: t(requiredKey) }}
            render={({ field }) => {
                const validationError = validateRow(allRows[rowIndex], rowIndex, allRows);
                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <DatePicker
                            value={field.value}
                            onChange={(val) => {
                                field.onChange(val);
                                onDateChange?.(val as Dayjs | null);
                            }}
                            inputRef={field.ref}
                            sx={{ mt: 0.5 }}
                            label={t(labelKey)}
                            disabled={loading || !!isLocked}
                            {...(isEndDate &&
                                !isLocked && {
                                    localeText: {
                                        okButtonLabel: t('timekeeper.employees.dialog.assign_pay_rule.set_indefinite'),
                                    },
                                })}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    error: !!validationError,
                                    helperText: validationError || '',
                                },
                                ...(isEndDate &&
                                    !isLocked && {
                                        actionBar: {
                                            actions: ['accept' as const],
                                            onAccept: () => field.onChange(INDEFINITE_DATE),
                                        },
                                    }),
                            }}
                        />
                    </LocalizationProvider>
                );
            }}
        />
    );
});

// Paper arrastrable

const PaperComponent = (props: PaperProps) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable nodeRef={nodeRef as RefObject<HTMLDivElement>} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
};

// Componente principal

export const AssignPayRule: React.FC<Props> = ({ open, setOpen, data, cleanSelected, reloading }) => {
    const { t } = useTranslation();
    const employeeId = data?.id;
    const hireDate = useMemo(() => dayjs(data?.created_at), [data?.created_at]);

    const [alerts, setAlerts] = useState(false);
    const [saving, setSaving] = useState(false);
    const idCounterRef = useRef(0);
    const addRowLockRef = useRef(false);
    const normalizedAtOpenRef = useRef(false);
    const [boundaryEdit, setBoundaryEdit] = useState<{ dotIdx: number; anchorEl: HTMLElement } | null>(null);
    const [segmentEdit, setSegmentEdit] = useState<{ formIndex: number; anchorEl: HTMLElement } | null>(null);

    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
        getValues,
        setValue,
    } = useForm<PayRuleFormValues>({
        defaultValues: { pay_rules: [] },
        mode: 'onChange',
    });

    const { fields, append, replace } = useFieldArray({ control, name: 'pay_rules' });

    // watchedRules mantiene las celdas y la validez reactivas ante cada cambio
    // useWatch garantiza suscripción reactiva a cambios anidados (pay_rule_id, fechas, etc.)
    const watchedRules = useWatch({ control, name: 'pay_rules' });

    // rows combina las identidades de campos (de useFieldArray) con los últimos valores observados
    const rows = useMemo<PayRuleRowWithIndex[]>(
        () =>
            fields.map((field, index) => ({
                ...field,
                ...(watchedRules?.[index] ?? {}),
                _index: index,
                _rowKey: field.id,
            })),
        [fields, watchedRules],
    );

    // sortedRows controla el orden de visualización del DataGrid y la línea de tiempo
    const sortedRows = useMemo<PayRuleRowWithIndex[]>(
        () =>
            [...rows].sort((a, b) => {
                const sa = toDayjs(a.start_date);
                const sb = toDayjs(b.start_date);
                if (!sa.isValid() || !sb.isValid()) return 0;
                return sa.valueOf() - sb.valueOf();
            }),
        [rows],
    );

    const firstSortedFormIndex = sortedRows[0]?._index ?? -1;
    const lastSortedFormIndex = sortedRows[sortedRows.length - 1]?._index ?? -1;

    const validateRow = useCallback(
        (row: PayRuleRow | undefined, index: number, allRows?: PayRuleRow[]): string | null => validateRowFn(row, index, allRows ?? getValues('pay_rules'), t),
        [getValues, t],
    );

    // El formulario es válido cuando todas las filas pasan la validación
    const isFormValid = useMemo(() => {
        if (!watchedRules || watchedRules.length === 0) return false;
        return watchedRules.every((row, i) => validateRowFn(row, i, watchedRules, t) === null);
    }, [watchedRules, t]);

    const buildNewRow = useCallback((): PayRuleRow => {
        idCounterRef.current += 1;
        return {
            id: idCounterRef.current,
            employee_r_pay_rule_id: null,
            employee_id: employeeId,
            pay_rule_id: '',
            start_date: hireDate.isValid() ? hireDate : dayjs(),
            end_date: INDEFINITE_DATE,
        };
    }, [employeeId, hireDate]);

    const normalizeBackendRows = useCallback(
        (backendRows: PayRuleRow[]): PayRuleRow[] => {
            if (!hireDate.isValid()) return backendRows;

            const sorted = [...backendRows].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());

            const rowsFromHireDate = sorted.filter((row) => {
                const rowEnd = toDayjs(row.end_date);
                return !rowEnd.isValid() || !rowEnd.isBefore(hireDate, 'day');
            });

            if (rowsFromHireDate.length === 0) return [];

            return rowsFromHireDate.map((row, index) => {
                if (index !== 0) return row;
                return {
                    ...row,
                    start_date: hireDate,
                };
            });
        },
        [hireDate],
    );

    const initRows = useCallback(
        (backendRows: PayRuleRow[]) => {
            const normalizedRows = normalizeBackendRows(backendRows);

            if (normalizedRows.length === 0) {
                idCounterRef.current = 0;
                reset({ pay_rules: [buildNewRow()] });
                return;
            }
            idCounterRef.current = normalizedRows.length;
            reset({ pay_rules: normalizedRows });
        },
        [buildNewRow, normalizeBackendRows, reset],
    );

    useEffect(() => {
        if (!open) {
            normalizedAtOpenRef.current = false;
            return;
        }
        if (normalizedAtOpenRef.current) return;
        if (!hireDate.isValid()) return;

        const currentRows = getValues('pay_rules');
        if (currentRows.length === 0) return;

        const normalizedRows = normalizeBackendRows(currentRows);
        normalizedAtOpenRef.current = true;

        if (normalizedRows.length === 0) {
            idCounterRef.current = 0;
            reset({ pay_rules: [buildNewRow()] });
            return;
        }

        const hasRowsBeforeHireDate = currentRows.some((row) => {
            const rowEnd = toDayjs(row.end_date);
            return rowEnd.isValid() && rowEnd.isBefore(hireDate, 'day');
        });
        const earliestCurrentStart = [...currentRows]
            .map((row) => toDayjs(row.start_date))
            .filter((d) => d.isValid())
            .sort((a, b) => a.valueOf() - b.valueOf())[0];
        const needsStartFix = !!earliestCurrentStart && !earliestCurrentStart.isSame(hireDate, 'day');

        if (hasRowsBeforeHireDate || needsStartFix) {
            idCounterRef.current = normalizedRows.length;
            reset({ pay_rules: normalizedRows });
        }
    }, [open, hireDate, getValues, normalizeBackendRows, reset, buildNewRow]);

    const { availablePayRules, loading: loadingData } = usePayRuleData({
        employeeId,
        open,
        onLoaded: initRows,
    });

    const loading = loadingData || saving;

    const closeForm = useCallback(() => {
        reset({ pay_rules: [] });
        setAlerts(false);
        cleanSelected();
        setOpen(false);
    }, [cleanSelected, reset, setOpen]);

    const handleClose = useCallback(() => {
        if (isDirty) setAlerts(true);
        else closeForm();
    }, [closeForm, isDirty]);

    // Ajuste automático cuando el usuario edita una fecha directamente en la tabla
    const handleDateChange = useCallback(
        (formIndex: number, fieldName: 'start_date' | 'end_date', newDate: Dayjs | null) => {
            if (!newDate?.isValid()) return;
            const allRows = getValues('pay_rules');

            if (fieldName === 'start_date') {
                // Re-normalizar end_dates según el nuevo orden ascendente de fechas inicio
                const withIdx = allRows.map((r, i) => ({
                    ...r,
                    _fi: i,
                    start_date: i === formIndex ? newDate : r.start_date,
                }));
                const sorted = [...withIdx].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());

                for (let i = 0; i < sorted.length - 1; i++) {
                    const nextStart = toDayjs(sorted[i + 1].start_date);
                    if (nextStart.isValid()) {
                        const newEnd = nextStart.subtract(1, 'day');
                        const rowStart = toDayjs(sorted[i].start_date);
                        // Sólo ajustar si end >= start (evitar rango inválido)
                        if (!newEnd.isBefore(rowStart)) {
                            setValue(`pay_rules.${sorted[i]._fi}.end_date`, newEnd, {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                        }
                    }
                }
                // La última fila siempre termina en 3000-01-01
                setValue(`pay_rules.${sorted[sorted.length - 1]._fi}.end_date`, INDEFINITE_DATE, {
                    shouldDirty: true,
                    shouldValidate: true,
                });
            } else {
                // end_date cambió: ajustar fecha inicio de la fila siguiente
                const withIdx = allRows.map((r, i) => ({ ...r, _fi: i }));
                const sorted = [...withIdx].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());
                const sortedPos = sorted.findIndex((r) => r._fi === formIndex);
                if (sortedPos >= 0 && sortedPos < sorted.length - 1) {
                    setValue(`pay_rules.${sorted[sortedPos + 1]._fi}.start_date`, newDate.add(1, 'day'), {
                        shouldDirty: true,
                        shouldValidate: true,
                    });
                }
            }
        },
        [getValues, setValue],
    );

    // Cambio de límite desde los puntos de la línea de tiempo
    const handleBoundaryChange = useCallback(
        (dotIdx: number, newDate: Dayjs) => {
            if (!newDate.isValid()) return;
            const prevRow = sortedRows[dotIdx - 1];
            const nextRow = sortedRows[dotIdx];
            if (!prevRow || !nextRow) return;
            setValue(`pay_rules.${prevRow._index}.end_date`, newDate.subtract(1, 'day'), {
                shouldDirty: true,
                shouldValidate: true,
            });
            setValue(`pay_rules.${nextRow._index}.start_date`, newDate, {
                shouldDirty: true,
                shouldValidate: true,
            });
            setBoundaryEdit(null);
        },
        [sortedRows, setValue],
    );

    const handleBoundaryClick = useCallback((dotIdx: number, anchorEl: HTMLElement) => setBoundaryEdit({ dotIdx, anchorEl }), []);
    const handleSegmentClick = useCallback((formIndex: number, anchorEl: HTMLElement) => setSegmentEdit({ formIndex, anchorEl }), []);

    const handleSegmentRuleChange = useCallback(
        (newRuleId: number | '') => {
            if (!segmentEdit) return;
            setValue(`pay_rules.${segmentEdit.formIndex}.pay_rule_id`, newRuleId, {
                shouldDirty: true,
                shouldValidate: true,
            });
            setSegmentEdit(null);
        },
        [segmentEdit, setValue],
    );

    const normalizeRowsAfterMutation = useCallback(
        (inputRows: PayRuleRow[], forceFirstStartToHireDate: boolean): PayRuleRow[] => {
            if (inputRows.length === 0) return inputRows;

            const rowsClone = inputRows.map((row) => ({ ...row }));
            const sorted = rowsClone
                .map((row, idx) => ({ row, idx }))
                .sort((a, b) => toDayjs(a.row.start_date).valueOf() - toDayjs(b.row.start_date).valueOf());

            if (forceFirstStartToHireDate && hireDate.isValid() && sorted[0]) {
                sorted[0].row.start_date = hireDate;
            }

            // Cascada de start_date para respetar duración mínima (end > start)
            // y permitir contigüidad estricta con end = nextStart - 1.
            for (let i = 1; i < sorted.length; i++) {
                const prevStart = toDayjs(sorted[i - 1].row.start_date);
                const currStart = toDayjs(sorted[i].row.start_date);
                if (!prevStart.isValid() || !currStart.isValid()) continue;

                const minCurrStart = prevStart.add(2, 'day');
                if (currStart.isBefore(minCurrStart, 'day')) {
                    sorted[i].row.start_date = minCurrStart;
                }
            }

            for (let i = 0; i < sorted.length - 1; i++) {
                const nextStart = toDayjs(sorted[i + 1].row.start_date);
                if (!nextStart.isValid()) continue;
                sorted[i].row.end_date = nextStart.subtract(1, 'day');
            }

            sorted[sorted.length - 1].row.end_date = INDEFINITE_DATE;
            return rowsClone;
        },
        [hireDate],
    );

    // addRowAfter: inserta una fila nueva después de la fila indicada.
    // - Si es la última fila, extiende la línea de tiempo como antes.
    // - Si está en medio, mueve en cascada los rangos siguientes para abrir espacio.
    const addRowAfter = useCallback((afterFormIndex: number) => {
        if (addRowLockRef.current) return;
        addRowLockRef.current = true;

        try {
            const allRows = getValues('pay_rules');
            const hasIncompleteRow = allRows.some((row) => !row.pay_rule_id);

            if (hasIncompleteRow) {
                return;
            }

            if (allRows.length === 0) {
                append(buildNewRow());
                return;
            }

            const withIdx = allRows.map((r, i) => ({ ...r, _fi: i }));
            const sorted = [...withIdx].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());
            const sortedPos = sorted.findIndex((r) => r._fi === afterFormIndex);

            if (sortedPos < 0) return;

            const currentRow = sorted[sortedPos];
            const isLastSorted = sortedPos === sorted.length - 1;

            if (isLastSorted) {
                const lastStart = toDayjs(currentRow.start_date);

                // Debe dejar al menos 1 día de duración en la fila previa: newStart >= lastStart + 2 días.
                const minNewStart = lastStart.add(2, 'day');
                const newStart = dayjs().isAfter(minNewStart, 'day') ? dayjs() : minNewStart;

                setValue(`pay_rules.${currentRow._fi}.end_date`, newStart.subtract(1, 'day'), {
                    shouldDirty: true,
                    shouldValidate: true,
                });

                idCounterRef.current += 1;
                append({
                    id: idCounterRef.current,
                    employee_r_pay_rule_id: null,
                    employee_id: employeeId,
                    pay_rule_id: '',
                    start_date: newStart,
                    end_date: INDEFINITE_DATE,
                });
                return;
            }

            const currentStart = toDayjs(currentRow.start_date);
            const currentEnd = toDayjs(currentRow.end_date);
            const nextRow = sorted[sortedPos + 1];
            const nextStart = toDayjs(nextRow?.start_date);

            if (!currentStart.isValid() || !currentEnd.isValid() || !nextStart.isValid()) {
                return;
            }

            // 1) Mantener la fila actual y empujar en cascada las filas siguientes,
            // para abrir espacio sin tocar el end_date del rango actual.
            for (let i = sortedPos + 1; i < sorted.length; i++) {
                const row = sorted[i];
                const rowStart = toDayjs(row.start_date);
                const rowEnd = toDayjs(row.end_date);

                if (rowStart.isValid()) {
                    setValue(`pay_rules.${row._fi}.start_date`, rowStart.add(2, 'day'), {
                        shouldDirty: true,
                        shouldValidate: true,
                    });
                }

                if (i < sorted.length - 1 && rowEnd.isValid()) {
                    setValue(`pay_rules.${row._fi}.end_date`, rowEnd.add(2, 'day'), {
                        shouldDirty: true,
                        shouldValidate: true,
                    });
                }
            }

            // 2) Insertar nuevo rango inmediatamente después del actual.
            const newStart = currentEnd.add(1, 'day');
            const newEnd = currentEnd.add(2, 'day');

            idCounterRef.current += 1;
            append({
                id: idCounterRef.current,
                employee_r_pay_rule_id: null,
                employee_id: employeeId,
                pay_rule_id: '',
                start_date: newStart,
                end_date: newEnd,
            });
        } finally {
            addRowLockRef.current = false;
        }
    }, [getValues, setValue, append, buildNewRow, employeeId]);

    // removeRow: rellena el hueco dejado por la fila eliminada para mantener cobertura completa
    const removeRow = useCallback(
        (formIndex: number) => {
            const allRows = getValues('pay_rules');
            if (allRows.length <= 1) return;

            const withIdx = allRows.map((r, i) => ({ ...r, _fi: i }));
            const sorted = [...withIdx].sort((a, b) => toDayjs(a.start_date).valueOf() - toDayjs(b.start_date).valueOf());
            const sortedPos = sorted.findIndex((r) => r._fi === formIndex);

            if (sortedPos < 0) return;

            const remainingRows = allRows.filter((_, idx) => idx !== formIndex);
            const normalizedRows = normalizeRowsAfterMutation(remainingRows, sortedPos === 0);
            replace(normalizedRows);
        },
        [getValues, normalizeRowsAfterMutation, replace],
    );

    // onSubmit: un único PATCH con la lista completa ordenada
    const onSubmit = useCallback(
        async (formData: PayRuleFormValues) => {
            try {
                setSaving(true);
                const formatDate = (v: Dayjs) => v.format('YYYY-MM-DD');

                const payload = formData.pay_rules.map((row) => ({
                    pay_rule_id: row.pay_rule_id as number,
                    start_date: formatDate(row.start_date as Dayjs),
                    end_date: formatDate(row.end_date as Dayjs),
                }));

                await employeeService.employee.setEmployeePayRules(employeeId, payload);

                enqueueSnackbar(t('timekeeper.employees.dialog.assign_pay_rule.alerts.success'), {
                    variant: 'success',
                });
                reloading();
                closeForm();
            } catch (error) {
                console.error('Error saving pay rules:', error);
                enqueueSnackbar(t('timekeeper.employees.dialog.assign_pay_rule.alerts.error'), {
                    variant: 'error',
                });
            } finally {
                setSaving(false);
            }
        },
        [closeForm, employeeId, reloading, t],
    );

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'actions',
                type: 'actions',
                headerName: t('timekeeper.employees.dialog.assign_pay_rule.actions_label'),
                flex: 0.5,
                headerAlign: 'center',
                align: 'center',
                getActions: (params: any) => {
                    const rowFormIndex: number = params.row._index;
                    const isOnlyRow = sortedRows.length === 1;
                    const actions: React.ReactElement[] = [];

                    if (!isOnlyRow) {
                        actions.push(
                            <GridActionsCellItem
                                key="delete"
                                icon={<DeleteIcon color="error" />}
                                label={t('general.buttons.delete')}
                                onClick={() => removeRow(rowFormIndex)}
                                disabled={loading}
                                showInMenu={false}
                            />,
                        );
                    }

                    actions.push(<GridActionsCellItem key="add" icon={<AddIcon color="action" />} label={t('general.buttons.add')} onClick={() => addRowAfter(rowFormIndex)} disabled={loading} showInMenu={false} />);

                    return actions;
                },
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'pay_rule_id',
                headerName: t('timekeeper.employees.dialog.assign_pay_rule.rule_label'),
                flex: 1.5,
                align: 'center',
                renderCell: (params: any) => (
                    <PayRuleSelectCell rowIndex={params.row._index} control={control} loading={loading} payRules={availablePayRules} validateRow={validateRow} t={t} allRows={watchedRules ?? []} />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'start_date',
                headerName: t('timekeeper.employees.dialog.assign_pay_rule.start_date_label'),
                flex: 1,
                align: 'center',
                renderCell: (params: any) => (
                    <DateCell
                        rowIndex={params.row._index}
                        fieldName="start_date"
                        control={control}
                        loading={loading}
                        validateRow={validateRow}
                        t={t}
                        allRows={watchedRules ?? []}
                        isLocked={params.row._index === firstSortedFormIndex}
                        onDateChange={(val) => handleDateChange(params.row._index, 'start_date', val)}
                    />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
            {
                field: 'end_date',
                headerName: t('timekeeper.employees.dialog.assign_pay_rule.end_date_label'),
                flex: 1,
                align: 'center',
                renderCell: (params: any) => (
                    <DateCell
                        rowIndex={params.row._index}
                        fieldName="end_date"
                        control={control}
                        loading={loading}
                        validateRow={validateRow}
                        t={t}
                        allRows={watchedRules ?? []}
                        isLocked={params.row._index === lastSortedFormIndex}
                        onDateChange={(val) => handleDateChange(params.row._index, 'end_date', val)}
                    />
                ),
                resizable: false,
                filterable: false,
                sortable: false,
                editable: false,
                groupable: false,
            },
        ],
        [t, sortedRows.length, removeRow, addRowAfter, handleDateChange, loading, control, availablePayRules, validateRow, watchedRules, firstSortedFormIndex, lastSortedFormIndex],
    );

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" PaperComponent={PaperComponent}>
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('timekeeper.employees.dialog.assign_pay_rule.title')}
            </DialogTitle>

            <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
                <CloseIcon />
            </IconButton>

            {alerts && (
                <Box sx={{ top: 0, zIndex: 10, pb: 1, px: 3, pt: 2 }}>
                    <Alert
                        variant="filled"
                        severity="warning"
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>
                                <Button onClick={closeForm} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                </Box>
            )}

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body1" gutterBottom>
                        {data?.full_name}
                    </Typography>

                    <Typography variant="body2" gutterBottom>
                        {t('timekeeper.employees.dialog.assign_pay_rule.assignments_label')}
                    </Typography>

                    {loading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <PayRuleTimeline
                                rows={sortedRows}
                                payRules={availablePayRules}
                                hireDate={hireDate}
                                onBoundaryClick={sortedRows.length > 1 ? handleBoundaryClick : undefined}
                                onSegmentClick={handleSegmentClick}
                            />

                            <DataGrid
                                rows={sortedRows}
                                getRowId={(row) => row._rowKey}
                                columns={columns}
                                density="standard"
                                disableColumnMenu
                                disableRowSelectionOnClick
                                hideFooter
                                hideFooterPagination
                                hideFooterSelectedRowCount
                                showCellVerticalBorder
                                showColumnVerticalBorder
                                rowHeight={80}
                                sx={{
                                    '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontSize: '0.875rem',
                                        fontWeight: 'bold',
                                        whiteSpace: 'normal',
                                        lineHeight: 1.2,
                                        textAlign: 'center',
                                        height: 'auto',
                                    },
                                    '& .MuiDataGrid-row': { transition: 'all 0.3s ease-in-out' },
                                }}
                            />
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={handleClose} disabled={loading}>
                    {t('general.buttons.close')}
                </Button>
                <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={loading || !isFormValid}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
            {boundaryEdit && (
                <Popover
                    open
                    anchorEl={boundaryEdit.anchorEl}
                    onClose={() => setBoundaryEdit(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <DateCalendar
                            value={(sortedRows[boundaryEdit.dotIdx]?.start_date as Dayjs) ?? null}
                            onChange={(val) => {
                                if (val && (val as Dayjs).isValid()) {
                                    handleBoundaryChange(boundaryEdit.dotIdx, val as Dayjs);
                                } else {
                                    setBoundaryEdit(null);
                                }
                            }}
                            minDate={(sortedRows[boundaryEdit.dotIdx - 1]?.start_date as Dayjs)?.add(2, 'day')}
                            maxDate={(sortedRows[boundaryEdit.dotIdx]?.end_date as Dayjs)?.subtract(1, 'day')}
                        />
                    </LocalizationProvider>
                </Popover>
            )}

            {segmentEdit && (
                <Popover
                    open
                    anchorEl={segmentEdit.anchorEl}
                    onClose={() => setSegmentEdit(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Box sx={{ p: 1.5, minWidth: 320 }}>
                        <Autocomplete
                            id="timeline-rule-edit"
                            autoHighlight
                            options={availablePayRules}
                            getOptionLabel={(option) => option.name}
                            value={availablePayRules.find((r) => r.pay_rule_id === getValues(`pay_rules.${segmentEdit.formIndex}.pay_rule_id`)) ?? null}
                            onChange={(_, newValue) => handleSegmentRuleChange(newValue?.pay_rule_id ?? '')}
                            disabled={loading}
                            fullWidth
                            size="small"
                            renderInput={(params) => <TextField {...params} label={t('timekeeper.employees.dialog.assign_pay_rule.rule_label')} />}
                        />
                    </Box>
                </Popover>
            )}
        </Dialog>
    );
};
